import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, ShieldCheck, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (phone: string) => void;
  initialPhone?: string;
}

type Step = "phone" | "otp";

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerified,
  initialPhone = "",
}) => {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState(initialPhone);
  const [otp, setOtp] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Start 60-second cooldown timer
  const startCooldown = () => {
    setCooldown(60);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (!phone.trim()) {
      toast({ title: "Please enter a phone number", variant: "destructive" });
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("phone-auth", {
        body: { action: "send", phone: phone.trim() },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setMaskedPhone(data.phone);
      setStep("otp");
      startCooldown();
      toast({
        title: "Verification code sent!",
        description: `We sent a 6-digit code to ${data.phone}`,
      });
    } catch (err: any) {
      toast({ title: "Could not send code", description: err.message, variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.trim().length !== 6) {
      toast({ title: "Please enter the 6-digit code", variant: "destructive" });
      return;
    }

    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("phone-auth", {
        body: { action: "verify", phone: phone.trim(), code: otp.trim() },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Phone number verified! ✅",
        description: "Your number has been confirmed successfully.",
      });

      onVerified(phone.trim());
      handleClose();
    } catch (err: any) {
      toast({ title: "Verification failed", description: err.message, variant: "destructive" });
      setOtp("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setOtp("");
    await handleSendOtp();
  };

  const handleClose = () => {
    setStep("phone");
    setPhone(initialPhone);
    setOtp("");
    setMaskedPhone("");
    setCooldown(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              {step === "phone" ? (
                <Phone className="h-5 w-5 text-blue-600" />
              ) : (
                <ShieldCheck className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <DialogTitle className="text-lg">
                {step === "phone" ? "Verify your phone number" : "Enter verification code"}
              </DialogTitle>
              <DialogDescription className="text-sm mt-0.5">
                {step === "phone"
                  ? "We'll send a 6-digit SMS code to confirm your number"
                  : `Code sent to ${maskedPhone}`}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* ── Step 1: Phone entry ── */}
          {step === "phone" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="pv-phone">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="pv-phone"
                    className="pl-8"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="083 123 4567"
                    type="tel"
                    onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  SA numbers: start with 0 (e.g. 083 123 4567) or +27
                </p>
              </div>

              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>Why we need this:</strong> Without a verified number our driver cannot reach
                  you if they cannot find your property, or if there are stock or delivery issues.
                  This keeps your orders moving smoothly.
                </p>
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={handleClose} className="flex-1" disabled={isSending}>
                  Cancel
                </Button>
                <Button onClick={handleSendOtp} className="flex-1" disabled={isSending}>
                  {isSending ? "Sending..." : "Send Code"}
                </Button>
              </div>
            </>
          )}

          {/* ── Step 2: OTP entry ── */}
          {step === "otp" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="pv-otp">
                  6-digit code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="pv-otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="text-center text-xl tracking-widest font-mono"
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Code expires in 10 minutes. Max 3 attempts.
                </p>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={handleResend}
                  className="flex-1"
                  disabled={cooldown > 0 || isSending}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  {cooldown > 0 ? `Resend (${cooldown}s)` : "Resend Code"}
                </Button>
                <Button onClick={handleVerifyOtp} className="flex-1" disabled={isVerifying}>
                  {isVerifying ? "Verifying..." : "Verify"}
                </Button>
              </div>

              <button
                type="button"
                onClick={() => {
                  setStep("phone");
                  setOtp("");
                }}
                className="w-full text-xs text-muted-foreground hover:text-foreground text-center pt-1"
              >
                ← Wrong number? Change it
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneVerificationModal;
